"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  Building2,
  FileText,
  Landmark,
  LifeBuoy,
  PlugZap,
  ShieldCheck,
  Store,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { branchesMultiFromSettingValue } from "@/lib/branches-setting";
import { cn } from "@/lib/utils";

type SettingRow = {
  id: number;
  key: string;
  value: string | null | number | boolean;
  type: string;
  group: string;
};

function TabIcon({
  children,
  className,
}: {
  children: React.ReactNode;
  className: string;
}) {
  return (
    <span
      className={`inline-flex h-6 w-6 items-center justify-center rounded-md text-white ${className}`}
    >
      {children}
    </span>
  );
}

export default function SettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["settings", "all"],
    queryFn: async () => {
      const { data: d } = await api.get<{ settings: SettingRow[] }>("/api/settings");
      return d.settings;
    },
    staleTime: 0,
  });

  const [draft, setDraft] = useState<Record<string, string>>({});

  const merged = useMemo(() => {
    const m: Record<string, string> = {};
    for (const r of data ?? []) {
      const v = r.value;
      m[r.key] = v === null || v === undefined ? "" : String(v);
    }
    return { ...m, ...draft };
  }, [data, draft]);

  const setKey = (key: string, value: string) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const saveGroup = useMutation({
    mutationFn: async (keys: string[]) => {
      const settings = keys.map((key) => ({ key, value: merged[key] ?? "" }));
      const { data: body } = await api.put<{ settings: SettingRow[] }>("/api/settings", { settings });
      return body.settings ?? [];
    },
    onSuccess: async (settings) => {
      qc.setQueryData(["settings", "all"], settings);
      toast.success("Settings saved");
      setDraft({});
    },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { error?: string } } };
      toast.error(ax.response?.data?.error ?? "Could not save settings.");
    },
  });

  const logoMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("logo", file);
      await api.post("/api/settings/logo", fd);
    },
    onSuccess: async () => {
      toast.success("Logo uploaded");
      await qc.invalidateQueries({ queryKey: ["settings", "all"] });
    },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { error?: string } } };
      toast.error(ax.response?.data?.error ?? "Logo upload failed.");
    },
  });

  const toggleBranchesEnabled = useMutation({
    mutationFn: async (next: boolean) => {
      const { data: body } = await api.put<{ settings: SettingRow[] }>("/api/settings", {
        settings: [{ key: "branches_enabled", value: next ? "1" : "0" }],
      });
      return body.settings ?? [];
    },
    onMutate: (next) => {
      setDraft((d) => ({ ...d, branches_enabled: next ? "1" : "0" }));
    },
    onSuccess: (settings) => {
      qc.setQueryData(["settings", "all"], settings);
      setDraft((d) => {
        const { branches_enabled: _b, ...rest } = d;
        return rest;
      });
      const on = branchesMultiFromSettingValue(
        settings.find((r) => r.key === "branches_enabled")?.value,
      );
      toast.success(on ? "Multi-branch enabled" : "Single-store mode enabled");
      void qc.invalidateQueries({ queryKey: ["branches"] });
    },
    onError: (e: unknown, _next, _ctx) => {
      setDraft((d) => {
        const { branches_enabled: _b, ...rest } = d;
        return rest;
      });
      void qc.invalidateQueries({ queryKey: ["settings", "all"] });
      const ax = e as { response?: { data?: { error?: string } } };
      toast.error(ax.response?.data?.error ?? "Could not save branch mode. Check settings.edit permission.");
    },
  });

  const branchesMultiOn = useMemo(
    () => branchesMultiFromSettingValue(merged.branches_enabled),
    [merged.branches_enabled],
  );

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading settings…</p>;
  }
  if (isError) {
    return (
      <p className="text-sm text-destructive">
        {(error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          "Could not load settings (check permissions: settings.view)."}
      </p>
    );
  }

  const field = (
    key: string,
    label: string,
    type: "text" | "number" | "textarea" = "text",
    className?: string,
  ) => (
    <div className={`grid gap-2 ${className ?? ""}`.trim()} key={key}>
      <Label htmlFor={key}>{label}</Label>
      {type === "textarea" ? (
        <Textarea
          id={key}
          rows={3}
          value={merged[key] ?? ""}
          onChange={(e) => setKey(key, e.target.value)}
        />
      ) : (
        <Input
          id={key}
          type={type === "number" ? "number" : "text"}
          value={merged[key] ?? ""}
          onChange={(e) => setKey(key, e.target.value)}
        />
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="max-w-5xl">
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage shop, invoice, tax, loyalty, integrations, backup.</p>
      </div>

      <Tabs defaultValue="general" className="flex max-w-5xl flex-col gap-4">
        {/* Top navbar style tabs */}
        <div className="sticky top-14 z-10 -mx-2 border-b bg-background/80 px-2 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <TabsList
            variant="line"
            className="w-full justify-start gap-2 overflow-x-auto rounded-none p-0 text-sm"
          >
            <TabsTrigger value="general" className="px-3 py-2">
              <TabIcon className="bg-indigo-600">
                <Building2 className="h-4 w-4" />
              </TabIcon>
              General
            </TabsTrigger>
            <TabsTrigger value="invoice" className="px-3 py-2">
              <TabIcon className="bg-emerald-600">
                <FileText className="h-4 w-4" />
              </TabIcon>
              Invoice
            </TabsTrigger>
            <TabsTrigger value="tax" className="px-3 py-2">
              <TabIcon className="bg-amber-600">
                <ShieldCheck className="h-4 w-4" />
              </TabIcon>
              Tax
            </TabsTrigger>
            <TabsTrigger value="loyalty" className="px-3 py-2">
              <TabIcon className="bg-pink-600">
                <LifeBuoy className="h-4 w-4" />
              </TabIcon>
              Loyalty
            </TabsTrigger>
            <TabsTrigger value="integrations" className="px-3 py-2">
              <TabIcon className="bg-cyan-600">
                <PlugZap className="h-4 w-4" />
              </TabIcon>
              Integrations
            </TabsTrigger>
            <TabsTrigger value="backup" className="px-3 py-2">
              <TabIcon className="bg-slate-700">
                <Landmark className="h-4 w-4" />
              </TabIcon>
              Backup
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="general" className="mt-4">
          <Card className="w-full">
            <CardHeader>
              <CardTitle>General</CardTitle>
              <CardDescription>Shop identity and display.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              {field("shop_name", "Shop name")}
              {field("shop_gstin", "GSTIN")}
              {field("shop_address", "Address", "textarea", "sm:col-span-2")}
              {field("shop_phone", "Phone")}
              {field("currency_symbol", "Currency symbol")}
              {field("date_format", "Date format")}
              <div className="relative overflow-hidden rounded-2xl border border-violet-200/60 bg-gradient-to-br from-violet-500/[0.12] via-card to-emerald-500/[0.08] p-5 shadow-md dark:border-violet-900/40 sm:col-span-2">
                <div
                  aria-hidden
                  className="pointer-events-none absolute -right-20 -top-20 size-56 rounded-full bg-violet-500/15 blur-3xl"
                />
                <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                  <div className="space-y-1.5 pr-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-violet-600 dark:text-violet-400">
                      Operations
                    </p>
                    <Label className="text-base font-semibold tracking-tight">Multi-location branches</Label>
                    <p className="max-w-xl text-sm text-muted-foreground leading-relaxed">
                      Single store hides branch pickers and uses your default location. Multi-branch enables POS /
                      product / purchase branch selection and Admin → Branches.
                    </p>
                  </div>
                  <div
                    className="flex w-full shrink-0 flex-col items-stretch gap-2 sm:max-w-md lg:w-auto lg:min-w-[280px]"
                    role="radiogroup"
                    aria-label="Branch mode"
                  >
                    <div className="flex rounded-full border border-border/80 bg-muted/50 p-1 shadow-inner dark:bg-muted/30">
                      <button
                        type="button"
                        role="radio"
                        aria-checked={!branchesMultiOn}
                        disabled={toggleBranchesEnabled.isPending}
                        onClick={() => {
                          if (!branchesMultiOn) {
                            return;
                          }
                          toggleBranchesEnabled.mutate(false);
                        }}
                        className={cn(
                          "flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-all duration-200 outline-none",
                          "focus-visible:ring-2 focus-visible:ring-violet-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                          !branchesMultiOn
                            ? "bg-gradient-to-br from-slate-700 to-slate-900 text-white shadow-md dark:from-slate-600 dark:to-slate-800"
                            : "text-muted-foreground hover:bg-background/60 hover:text-foreground",
                        )}
                      >
                        <Store className="h-4 w-4 shrink-0 opacity-90" />
                        Single store
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={branchesMultiOn}
                        disabled={toggleBranchesEnabled.isPending}
                        onClick={() => {
                          if (branchesMultiOn) {
                            return;
                          }
                          toggleBranchesEnabled.mutate(true);
                        }}
                        className={cn(
                          "flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-all duration-200 outline-none",
                          "focus-visible:ring-2 focus-visible:ring-violet-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                          branchesMultiOn
                            ? "bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-md"
                            : "text-muted-foreground hover:bg-background/60 hover:text-foreground",
                        )}
                      >
                        <Building2 className="h-4 w-4 shrink-0 opacity-90" />
                        Multi-branch
                      </button>
                    </div>
                    <p className="text-center text-[11px] text-muted-foreground">
                      {toggleBranchesEnabled.isPending ? "Saving…" : branchesMultiOn ? "Multi-branch is on." : "Single-store mode is on."}
                    </p>
                  </div>
                </div>
              </div>
              <div className="grid gap-2 sm:col-span-2">
                <Label>Shop logo</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      logoMutation.mutate(f);
                    }
                  }}
                />
                {merged.shop_logo_url ? (
                  <p className="text-xs text-muted-foreground break-all">{merged.shop_logo_url}</p>
                ) : null}
              </div>
              <div className="sm:col-span-2">
                <Button
                type="button"
                disabled={saveGroup.isPending}
                onClick={() =>
                  saveGroup.mutate([
                    "shop_name",
                    "shop_gstin",
                    "shop_address",
                    "shop_phone",
                    "currency_symbol",
                    "date_format",
                  ])
                }
              >
                Save general
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoice" className="mt-4">
          <Card className="w-full">
            <CardHeader>
              <CardTitle>Invoice</CardTitle>
              <CardDescription>Prefix, footer, template, bank JSON.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Template</Label>
                <Select
                  value={merged.invoice_template ?? "1"}
                  onValueChange={(v) => setKey("invoice_template", v ?? "1")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Design 1</SelectItem>
                    <SelectItem value="2">Design 2</SelectItem>
                    <SelectItem value="3">Design 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {field("invoice_prefix", "Invoice prefix")}
              {field("invoice_footer", "Invoice footer", "textarea", "sm:col-span-2")}
              {field("invoice_bank_details", "Bank details (JSON)", "textarea", "sm:col-span-2")}
              <div className="sm:col-span-2">
                <Button
                type="button"
                disabled={saveGroup.isPending}
                onClick={() =>
                  saveGroup.mutate(["invoice_template", "invoice_prefix", "invoice_footer", "invoice_bank_details"])
                }
              >
                Save invoice
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tax" className="mt-4">
          <Card className="w-full">
            <CardHeader>
              <CardTitle>Tax</CardTitle>
              <CardDescription>GST and rounding.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              {field("gst_state_code", "State code")}
              {field("tax_rounding", "Round-off method")}
              <div className="flex items-center gap-2 sm:col-span-2">
                <input
                  id="tax_inclusive"
                  type="checkbox"
                  checked={merged.tax_inclusive === "1" || merged.tax_inclusive === "true"}
                  onChange={(e) => setKey("tax_inclusive", e.target.checked ? "1" : "0")}
                />
                <Label htmlFor="tax_inclusive">Tax inclusive pricing</Label>
              </div>
              <div className="sm:col-span-2">
                <Button
                type="button"
                disabled={saveGroup.isPending}
                onClick={() => saveGroup.mutate(["gst_state_code", "tax_rounding", "tax_inclusive"])}
              >
                Save tax
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="loyalty" className="mt-4">
          <Card className="w-full">
            <CardHeader>
              <CardTitle>Loyalty</CardTitle>
              <CardDescription>Points earn/redeem and tier thresholds.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              {field("loyalty_points_per_rupee", "Points per ₹", "number")}
              {field("loyalty_rupees_per_point", "₹ per point", "number")}
              {field("loyalty_tier_silver", "Silver threshold (points)", "number")}
              {field("loyalty_tier_gold", "Gold threshold (points)", "number")}
              <div className="sm:col-span-2">
                <Button
                type="button"
                disabled={saveGroup.isPending}
                onClick={() =>
                  saveGroup.mutate([
                    "loyalty_points_per_rupee",
                    "loyalty_rupees_per_point",
                    "loyalty_tier_silver",
                    "loyalty_tier_gold",
                  ])
                }
              >
                Save loyalty
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="integrations" className="mt-4">
          <Card className="w-full">
            <CardHeader>
              <CardTitle>Integrations</CardTitle>
              <CardDescription>WhatsApp, SMS, SMTP.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              {field("whatsapp_api_url", "WhatsApp API URL")}
              {field("whatsapp_api_key", "WhatsApp API key")}
              {field("sms_api_key", "SMS API key")}
              {field("smtp_host", "SMTP host")}
              {field("smtp_port", "SMTP port", "number")}
              {field("smtp_user", "SMTP user")}
              <div className="grid gap-2 sm:col-span-2">
                <Label htmlFor="smtp_pass">SMTP password</Label>
                <Input
                  id="smtp_pass"
                  type="password"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  value={merged.smtp_pass ?? ""}
                  onChange={(e) => setKey("smtp_pass", e.target.value)}
                />
              </div>
              <div className="sm:col-span-2">
                <Button
                type="button"
                disabled={saveGroup.isPending}
                onClick={() =>
                  saveGroup.mutate([
                    "whatsapp_api_url",
                    "whatsapp_api_key",
                    "sms_api_key",
                    "smtp_host",
                    "smtp_port",
                    "smtp_user",
                    "smtp_pass",
                  ])
                }
              >
                Save integrations
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="backup" className="mt-4">
          <Card className="w-full">
            <CardHeader>
              <CardTitle>Backup</CardTitle>
              <CardDescription>Manual export / restore (planned).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Database backup download and restore upload will connect to a future maintenance endpoint.
              </p>
              <Button type="button" variant="secondary" disabled>
                Download backup
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
